import { useCallback, useEffect, useState } from "react"
import api from "../../utils/api"
import Icon from "../../components/Icon"
import ConfirmModal from "../../components/ConfirmModal"
import { formatDateShort } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { WAITING_FOR_TENANT_APPROVAL } from "./tenancyInvitationDisplay"

// Shown on a vacant unit while its assigned tenant has not yet approved and paid.
// The manager can cancel the assignment to assign someone else. It sits below
// the unit actions, so nothing moves under the manager's finger when it appears.
function UnitInvitationNotice({ unitId, refreshKey = 0, onChanged }) {
  const [invitation, setInvitation] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    if (!unitId) {
      setInvitation(null)
      return
    }
    try {
      const response = await api.get(`/frp/tenancy-invitations/sent?unitId=${encodeURIComponent(unitId)}`)
      const list = Array.isArray(response?.data?.invitations) ? response.data.invitations : []
      setInvitation(list[0] || null)
    } catch {
      setInvitation(null)
    }
  }, [unitId])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  if (!invitation) return null
  const name = invitation.tenantName || "the tenant"

  const cancel = async () => {
    setBusy(true)
    setError("")
    try {
      await api.post(`/frp/tenancy-invitations/${encodeURIComponent(invitation._id)}/cancel`)
      setInvitation(null)
      onChanged?.()
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not cancel the assignment."))
    } finally {
      setConfirmOpen(false)
      setBusy(false)
    }
  }

  return (
    <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-amber-900">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <Icon name="hourglass-split" size="0.8rem" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold sm:text-sm">{WAITING_FOR_TENANT_APPROVAL}</p>
          <p className="mt-0.5 text-[11px] sm:text-xs">
            {name} must approve and pay in their own account. Assigned {formatDateShort(invitation.sentAt)} ·
            move-in {formatDateShort(invitation.moveInDate)}. Nothing is charged until then.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="shrink-0 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-amber-800 transition-colors hover:bg-amber-100"
        >
          Cancel
        </button>
      </div>
      {error && <p role="alert" className="mt-2 text-xs font-medium text-red-700">{error}</p>}

      <ConfirmModal
        open={confirmOpen}
        title="Cancel this assignment?"
        message={`${name} will no longer be able to approve and pay. You can then assign someone else.`}
        icon="x-circle-fill"
        tone="rose"
        confirmLabel="Cancel assignment"
        cancelLabel="Keep it"
        busy={busy}
        onConfirm={cancel}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  )
}

export default UnitInvitationNotice
